import { bridgeKeyboardFocus } from "@salestrak/ui";
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { Editor } from "@tiptap/react";

/** What the docked bar is writing besides a new comment. */
export type DockedComposerTarget =
  // A new comment started away from the Activity tab (the Summary's
  // Comments widget): it calls the bar up there, and an empty blur lets it go.
  | { kind: "comment" }
  | { kind: "reply"; parentNoteId: string; name: string | null }
  | { kind: "edit"; noteId: string; content: string };

type DockedComposerContextValue = {
  target: DockedComposerTarget | null;
  compose: () => void;
  reply: (parentNoteId: string, name: string | null) => void;
  edit: (noteId: string, content: string) => void;
  clear: () => void;
  /** The bar hands its editor over so a press elsewhere can focus it inside
   *  the tap — the only focus iOS raises the keyboard for. */
  registerEditor: (editor: Editor | null) => void;
};

const DockedComposerContext = createContext<DockedComposerContextValue | null>(null);

/**
 * The phone sheet's one comment bar, reachable from the feed: Reply and Edit
 * retarget the bar on the keyboard instead of opening a second composer deep
 * in the feed, where its buttons sink under the keys. Absent (null) on every
 * other host, which keeps its in-place composers.
 */
export function DockedComposerProvider({ children }: { children: React.ReactNode }) {
  const [target, setTarget] = useState<DockedComposerTarget | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const pendingFocusRef = useRef(false);

  const focusBar = useCallback(() => {
    const editor = editorRef.current;
    if (editor && !editor.isDestroyed) {
      editor.commands.focus("end");
      return;
    }
    // The bar is not mounted yet (another tab): a stand-in input holds the
    // keyboard, and the editor takes the focus over as it registers.
    pendingFocusRef.current = true;
    bridgeKeyboardFocus();
  }, []);

  const compose = useCallback(() => {
    setTarget((current) => current ?? { kind: "comment" });
    focusBar();
  }, [focusBar]);

  const reply = useCallback(
    (parentNoteId: string, name: string | null) => {
      setTarget({ kind: "reply", parentNoteId, name });
      focusBar();
    },
    [focusBar],
  );

  const edit = useCallback(
    (noteId: string, content: string) => {
      setTarget({ kind: "edit", noteId, content });
      focusBar();
    },
    [focusBar],
  );

  const clear = useCallback(() => setTarget(null), []);

  const registerEditor = useCallback((editor: Editor | null) => {
    editorRef.current = editor;
    if (editor && pendingFocusRef.current) {
      pendingFocusRef.current = false;
      editor.commands.focus("end");
    }
  }, []);

  const value = useMemo(
    () => ({ target, compose, reply, edit, clear, registerEditor }),
    [target, compose, reply, edit, clear, registerEditor],
  );

  return (
    <DockedComposerContext.Provider value={value}>{children}</DockedComposerContext.Provider>
  );
}

export function useDockedComposer(): DockedComposerContextValue | null {
  return useContext(DockedComposerContext);
}
